import { useCallback } from 'react';
import { useQuery } from '@apollo/client';

import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import useCompanyContext from 'common/hooks/useCompanyContext';
import SuspenseQuery from 'components/SuspenseQuery';

import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import { CompanyCreationDocumentsModuleDataDocument } from './graphql/queries/companyCreationDocumentsModuleData.gql';
import getSelectedDocument from './libs/getSelectedDocument';
import machineConfig, { model, State } from './machine';
import DocumentUpload from './pages/DocumentUpload';
import HandwrittenIdentityInfo from './pages/HandwrittenIdentityInfo';
import HandwrittenIdentityUpload from './pages/HandwrittenIdentityUpload';
import Home from './pages/Home';

const CompanyCreationDocumentsComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;
    const { viewer } = moduleData.data;

    const stringifiedState = stringifyState<State>(state);

    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    switch (stringifiedState) {
      case State.HOME: {
        return (
          <Home
            documents={[...viewer.application.companyCreationDocuments].sort(
              (a, b) => a.type.localeCompare(b.type),
            )}
            onDocumentClick={(documentType) =>
              send(model.events.SELECT_DOCUMENT(documentType))
            }
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.DOCUMENT_UPLOAD: {
        const { selectedDocumentType } = state.context;

        const document = getSelectedDocument({
          moduleData,
          selectedDocumentType,
        });

        if (!document) {
          throw Error(
            `Cannot access ${state} state without a selected document`,
          );
        }

        return (
          <DocumentUpload
            document={document}
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.HANDWRITTEN_IDENTITY_INFO: {
        return (
          <HandwrittenIdentityInfo
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.HANDWRITTEN_IDENTITY_UPLOAD: {
        const { selectedDocumentType } = state.context;

        const selectedDocument = getSelectedDocument({
          moduleData,
          selectedDocumentType,
        });

        if (!selectedDocument) {
          throw Error(
            `Cannot access ${state} state without a selected document`,
          );
        }

        return (
          <HandwrittenIdentityUpload
            document={selectedDocument}
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.ABORT:
      case State.DONE:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },

  { devTools: true, machineConfig },
);

interface CompanyCreationDocumentsProps {
  companyProfileId: string;
}
const CompanyCreationDocuments = asBentoModule<CompanyCreationDocumentsProps>(
  ({ companyProfileId, onDone }) => {
    const query = useQuery(CompanyCreationDocumentsModuleDataDocument, {
      notifyOnNetworkStatusChange: true,
      variables: {
        companyProfileId,
      },
    });

    const companyContext = useCompanyContext();

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={query}
      >
        {(data) => {
          return (
            <CompanyCreationDocumentsComponent
              moduleData={{
                companyContext,
                data: data,
              }}
              onDone={onDone}
            />
          );
        }}
      </SuspenseQuery>
    );
  },
);

export default CompanyCreationDocuments;
